<template>
  <div class="notifications_view--settings_block">
    <div class="inputs-container" style="min-width: 15rem;">
      <strong> {{title}} </strong>
      <div v-for="(input) in formFields" :key="`${input.identifiers.key}-${key(input.identifiers)}-${input.value}`">
        <div v-if="input.readOnly === true">
          {{ input.label }}: {{ input.value || 'false' }}
        </div>
        <div v-else>
          <form-checkbox-input
            v-if="input.type === 'checkbox'"
            :label="input.label"
            :value="input.value"
            :identifier="uniqueIdentifier(input.identifiers)"
            :id="uniqueIdentifier(input.identifiers)"
            :options="input.options"
            @change="handleInput($event, input.identifiers)"/>
          <form-text-input
            v-if="input.type === 'text'"
            :label="input.label"
            :initialValue="input.value"
            :id="uniqueIdentifier(input.identifiers)"
            :identifier="uniqueIdentifier(input.identifiers)"
            :optional="input.optional"
            @change="handleInput($event, input.identifiers)"/>
          <form-select-input
            v-if="input.type === 'select'"
            :label="input.label"
            :initialValue="input.value"
            :multiple="input.multiple"
            :id="uniqueIdentifier(input.identifiers)"
            :identifier="uniqueIdentifier(input.identifiers)"
            :options="input.options"
            :expandable="input.expandable"
            @change="handleInput($event, input.identifiers)"/>
          <form-number-input
            v-if="input.type === 'number'"
            :label="input.label"
            :initialValue="input.value"
            :min="input.min"
            :max="input.max"
            :id="uniqueIdentifier(input.identifiers)"
            :identifier="uniqueIdentifier(input.identifiers)"
            @change="handleInput($event, input.identifiers)"/>
        </div>
      </div>
    </div>
      <div class="buttons-n-stuff" style="margin-left: 1rem;">
        <form-group-button
          v-if="canCopy"
          icon="copy"
          tooltip="copy"
          @click="copyConfiguration"
          />
        <form-group-button
          v-if="canPaste"
          icon="paste"
          tooltip="paste"
          @click="pasteConfiguration"
          />
        <form-group-button
          v-if="canDelete"
          icon="trash"
          tooltip="delete"
          @click="deactivateConfiguration"
          />
      </div>
  </div>
</template>

<script>
import FormTextInput from './FormTextInput'
import FormCheckboxInput from './FormCheckboxInput'
import FormSelectInput from './FormSelectInput'
import FormNumberInput from './FormNumberInput'
import FormGroupButton from './FormGroupButton'

export default {
  name: 'FormGroup',
  props: {
    title: {
      type: String,
    },
    formFields: {
      type: Array,
    },
    initialValue: {
      type: Object,
    },
    identifiers: {
      type: Object,
    },
    pasteCallback: {
      type: Function,
    },
    canCopy: {
      type: Boolean,
      default: true,
    },
    canPaste: {
      type: Boolean,
      default: true,
    },
    canDelete: {
      type: Boolean,
      default: true,
    },
  },
  data () {
    return {
      values: {},
    }
  },
  components: {
    FormTextInput,
    FormCheckboxInput,
    FormSelectInput,
    FormNumberInput,
    FormGroupButton,
  },
  methods: {
    key (identifiers) {
      return Object.entries(identifiers).filter(pair => pair[0] !== 'key')[0][1]
    },
    handleInput (value, identifiers) {
      const formField = this.formFields.filter(field => field.identifiers.key === identifiers.key)[0]
      let valid = true
      let needsValidationAndFormatting = !formField.optional || value != null
      if (formField.validator && needsValidationAndFormatting) {
        valid = formField.validator(value)
      }
      if (valid) {
        let output = value
        if (formField.formatter && needsValidationAndFormatting) {
          output = formField.formatter(value)
        }
        const formGroupValues = this.collectCurrentFormInputs()
        this.values = { ...formGroupValues, [identifiers.key]: output }
        const change = {
          group: this.identifiers.key,
          parentId: this.parentId,
          values: this.values,
        }
        const element = document.getElementById(`${identifiers.key}-${this.key(identifiers)}`)
        element.style.border = ''
        this.$emit('change', change)
      } else {
        const element = document.getElementById(`${identifiers.key}-${this.key(identifiers)}`)
        element.style.border = '1.5px solid rgba(255, 0, 0, 0.5)'
      }
    },
    collectCurrentFormInputs () {
      return this.formFields.reduce((prev, formField) => {
        let needsValidationAndFormatting = !formField.optional || formField.value != null
        if (formField.formatter && needsValidationAndFormatting) {
          prev[formField.identifiers.key] = formField.formatter(formField.value)
        } else {
          prev[formField.identifiers.key] = formField.value
        }
        return prev
      }, {})
    },
    copyConfiguration () {
      const formGroupValues = this.collectCurrentFormInputs()
      const copyData = {
        identifiers: this.identifiers,
        formGroupValues,
      }
      this.$emit('copy', copyData)
    },
    pasteConfiguration () {
      const pasteData = this.pasteCallback()
      if (this.identifiers.key === pasteData.identifiers.key) {
        const change = {
          group: this.identifiers.key,
          parentId: this.parentId,
          values: pasteData.formGroupValues,
        }
        this.$emit('change', change)
      } else {
        console.error('Clipboard configuration type does not match attempted configuration.')
      }
    },
    deactivateConfiguration () {
      const change = {
        group: 'deactivate',
        parentId: this.parentId,
      }
      this.$emit('deactivate', change)
    },
    uniqueIdentifier (identifiers) {
      return `${identifiers.key}-${this.key(identifiers)}`
    },
  },
  computed: {
    parentId () {
      const sampleFormField = this.formFields[0]
      return Object.entries(sampleFormField.identifiers).filter(identifier => identifier[0] !== 'key')[0]
    },
  },
  mounted () {
    this.values = this.initialValues
  },
}
</script>

<style>
  .notifications_view--settings_block {
    margin-bottom: 1rem;
    display: flex;
    flex-direction: row;
  }

  input, select {
    border: 1px solid rgba(34,36,38,.15);
    border-radius: 0.35rem;
  }
</style>
